"use client"

import React, { useEffect, useState, useMemo, useCallback } from 'react'

// ============================================================================
// Types
// ============================================================================

export interface TocItem {
  id: string
  text: string
  level: number // 1 for h1, 2 for h2, 3 for h3
  children?: TocItem[] // Nested sub-headings
}

interface TableOfContentsProps {
  content: string
  minHeadings?: number // Minimum headings needed to show TOC (default: 2)
}

// ============================================================================
// Constants
// ============================================================================

const SCROLL_THRESHOLD = 100 // Pixels from top to consider heading "active"
const DESKTOP_SCROLL_MARGIN = '96px'
const MOBILE_SCROLL_MARGIN = '375px' // Extra space for fixed header + mobile TOC

// ============================================================================
// Utility Functions
// ============================================================================

/** Generate a URL-friendly slug from heading text (matches Rails parameterize) */
function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/[\s_-]+/g, '-') // Replace spaces, underscores, hyphens with single hyphen
    .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
}

/** Parse headings from HTML content - SSR compatible */
function parseHeadings(html: string): TocItem[] {
  const headings: TocItem[] = []

  // Use DOMParser in browser, regex fallback for SSR
  if (typeof DOMParser !== 'undefined') {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    const allHeadings = doc.querySelectorAll('h1, h2, h3')

    allHeadings.forEach((el) => {
      const tagName = el.tagName.toLowerCase()
      const text = el.textContent?.trim() || ''
      if (!text) return

      const existingId = el.id || generateSlug(text)
      const level = tagName === 'h1' ? 1 : tagName === 'h2' ? 2 : 3

      // Handle duplicate IDs by appending level and count
      const id = headings.some(h => h.id === existingId)
        ? `${existingId}-${level}-${headings.filter(h => h.level === level).length}`
        : existingId

      headings.push({ id, text, level })
    })

    return headings
  }

  // SSR fallback: regex parsing
  const allHeadingsRegex = /<h([1-3])[^>]*>([\s\S]*?)<\/h\1>/gi
  let match
  const levelCounts = { 1: 0, 2: 0, 3: 0 }

  while ((match = allHeadingsRegex.exec(html)) !== null) {
    const level = parseInt(match[1])
    const innerContent = match[2]
    const text = innerContent.replace(/<[^>]+>/g, '').trim()

    if (!text) continue

    const existingId = generateSlug(text)
    const id = headings.some(h => h.id === existingId)
      ? `${existingId}-${level}-${levelCounts[level as keyof typeof levelCounts]++}`
      : existingId

    headings.push({ id, text, level })
  }

  return headings
}

/** Build hierarchical structure from flat heading list */
function buildHierarchicalHeadings(headings: TocItem[]): TocItem[] {
  const result: TocItem[] = []
  let currentH1: TocItem | null = null
  let currentH2: TocItem | null = null

  headings.forEach(item => {
    if (item.level === 1) {
      currentH1 = { ...item, children: [] }
      currentH2 = null
      result.push(currentH1)
    } else if (item.level === 2) {
      const newH2: TocItem = { ...item, children: [] }
      currentH2 = newH2
      if (currentH1?.children) {
        currentH1.children.push(newH2)
      } else {
        result.push(newH2)
      }
    } else if (item.level === 3) {
      const newH3: TocItem = { ...item }
      if (currentH2?.children) {
        currentH2.children.push(newH3)
      } else if (currentH1?.children) {
        currentH1.children.push(newH3)
      } else {
        result.push(newH3)
      }
    }
  })

  return result
}

/** Inject IDs and scroll-margin-top into heading elements */
function injectHeadingIds(html: string, headings: TocItem[], isMobile: boolean): string {
  const scrollMargin = isMobile ? MOBILE_SCROLL_MARGIN : DESKTOP_SCROLL_MARGIN
  let modifiedHtml = html

  headings.forEach(({ id, text, level }) => {
    const tag = `h${level}`
    const plainText = text.replace(/<[^>]+>/g, '').trim()

    // Pattern to match heading with any attributes
    const regexWithAttrs = new RegExp(`<${tag}([^>]*)>[\\s\\S]*?<\\/${tag}>`, 'gi')
    // Pattern to match heading without any attributes
    const regexPlain = new RegExp(`<${tag}>[\\s\\S]*?<\\/${tag}>`, 'gi')

    // Handle headings with existing attributes
    modifiedHtml = modifiedHtml.replace(regexWithAttrs, (match, attrs) => {
      const innerMatch = match.match(new RegExp(`<${tag}[^>]*>([\\s\\S]*?)<\\/${tag}>`, 'i'))
      if (!innerMatch) return match

      const innerContent = innerMatch[1]
      const innerText = innerContent.replace(/<[^>]+>/g, '').trim()
      if (innerText !== plainText) return match

      // Check if id already exists
      const hasId = attrs.includes('id="') || attrs.includes("id='")
      const hasStyle = attrs.includes('style="') || attrs.includes("style='")

      if (hasId && hasStyle && attrs.includes('scroll-margin-top')) {
        return match // Already has everything we need
      }

      const newAttrs = addAttributesToTag(tag, attrs, id, scrollMargin, hasId, hasStyle)
      return `${newAttrs}>${innerContent}</${tag}>`
    })

    // Handle plain headings (no attributes)
    modifiedHtml = modifiedHtml.replace(regexPlain, (match) => {
      const innerMatch = match.match(/<h\d>([\s\S]*?)<\/h\d>/i)
      if (!innerMatch) return match

      const innerContent = innerMatch[1]
      const innerText = innerContent.replace(/<[^>]+>/g, '').trim()
      if (innerText !== plainText) return match

      return `<${tag} id="${id}" style="scroll-margin-top: ${scrollMargin}">${innerContent}</${tag}>`
    })
  })

  return modifiedHtml
}

/** Helper to add id and style attributes to a tag */
function addAttributesToTag(
  tag: string,
  existingAttrs: string,
  id: string,
  scrollMargin: string,
  hasId: boolean,
  hasStyle: boolean
): string {
  let attrs = existingAttrs.trim()

  if (!hasId) {
    attrs += ` id="${id}"`
  }

  if (!hasStyle) {
    attrs += ` style="scroll-margin-top: ${scrollMargin}"`
  } else {
    // Append scroll-margin-top to existing style
    attrs = attrs.replace(/style="([^"]*)"/, (_, style) => {
      const cleanStyle = style.trim().endsWith(';') ? style.trim() : style.trim() + ';'
      return `style="${cleanStyle} scroll-margin-top: ${scrollMargin}"`
    })
  }

  return `<${tag}${attrs}`
}

// ============================================================================
// Custom Hooks
// ============================================================================

/** Track which heading is currently visible in viewport */
function useActiveHeading(headings: TocItem[]) {
  const [activeId, setActiveId] = useState<string>('')

  useEffect(() => {
    const handleScroll = () => {
      const headingElements = headings
        .map(h => document.getElementById(h.id))
        .filter(Boolean) as HTMLElement[]

      // Find the first heading that's above or near the viewport
      for (let i = headingElements.length - 1; i >= 0; i--) {
        const element = headingElements[i]
        const rect = element.getBoundingClientRect()
        if (rect.top <= SCROLL_THRESHOLD) {
          setActiveId(headings[i].id)
          return
        }
      }

      // Default to first heading if none are visible
      if (headingElements.length > 0) {
        setActiveId(headings[0].id)
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [headings])

  return activeId
}

// ============================================================================
// Components
// ============================================================================

/** Single TOC item - renders recursively for nested items */
function TocItemComponent({
  item,
  activeId,
  onClick,
}: {
  item: TocItem
  activeId: string
  onClick: (id: string) => void
}) {
  const hasChildren = item.children && item.children.length > 0
  const isActive = activeId === item.id

  const indentClass = item.level === 2 ? 'ml-4' : item.level === 3 ? 'ml-8' : ''

  return (
    <li key={item.id} className={`my-1 ${indentClass}`}>
      <a
        href={`#${item.id}`}
        onClick={(e) => {
          e.preventDefault()
          onClick(item.id)
        }}
        className={`block py-1.5 px-2 text-sm no-underline rounded transition-colors ${
          isActive
            ? 'text-foreground bg-primary/20 border-l-2 border-primary font-medium'
            : 'text-foreground/70 hover:text-foreground hover:bg-muted'
        }`}
      >
        {item.text}
      </a>
      {hasChildren && (
        <ul className="list-none m-0 p-0">
          {item.children!.map((child) => (
            <TocItemComponent
              key={child.id}
              item={child}
              activeId={activeId}
              onClick={onClick}
            />
          ))}
        </ul>
      )}
    </li>
  )
}

/** Shared render logic for both mobile and desktop TOC */
function TableOfContentsContent({
  headings,
  activeId,
  onHeadingClick,
  isExpanded,
  onToggle,
  isMobile,
}: {
  headings: TocItem[]
  activeId: string
  onHeadingClick: (id: string) => void
  isExpanded: boolean
  onToggle: () => void
  isMobile: boolean
}) {
  const hierarchicalHeadings = useMemo(
    () => buildHierarchicalHeadings(headings),
    [headings]
  )

  // Mobile-specific toggle button
  const ToggleButton = (
    <button
      className="flex items-center justify-between w-full bg-muted text-foreground px-4 py-3 rounded-lg transition-colors"
      onClick={onToggle}
      aria-expanded={isExpanded}
      aria-label="Toggle table of contents"
    >
      <span className="flex items-center gap-2 text-sm font-medium">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
        Contents
      </span>
      <svg
        className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  )

  // Shared nav content
  const NavContent = (
    <nav
      className={isMobile ? 'mt-2 bg-card border rounded-lg p-3 max-h-64 overflow-y-auto' : ''}
      aria-label="Table of contents"
    >
      <ul className="list-none m-0 p-0">
        {hierarchicalHeadings.map((heading) => (
          <TocItemComponent
            key={heading.id}
            item={heading}
            activeId={activeId}
            onClick={onHeadingClick}
          />
        ))}
      </ul>
    </nav>
  )

  if (isMobile) {
    return (
      <div className="lg:hidden mb-6 sticky top-16 z-20">
        {ToggleButton}
        {isExpanded && NavContent}
      </div>
    )
  }

  // Desktop TOC - always expanded (not collapsible)
  return (
    <nav
      className="hidden lg:block bg-card border rounded-lg p-4 max-h-[calc(100vh-10rem)] overflow-y-auto sticky top-20 lg:top-24 z-20"
      aria-label="Table of contents"
    >
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
        On this page
      </h3>
      {NavContent}
    </nav>
  )
}

// ============================================================================
// Desktop TOC - Sticky sidebar (shown on lg+ screens)
// ============================================================================

function DesktopTableOfContents({ content, minHeadings = 2 }: TableOfContentsProps) {
  const [isExpanded, setIsExpanded] = useState(true)

  const headings = useMemo(() => parseHeadings(content), [content])
  const activeId = useActiveHeading(headings)

  // Don't render if not enough headings
  if (headings.length < minHeadings) {
    return null
  }

  const handleClick = useCallback((id: string) => {
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      window.history.pushState(null, '', `#${id}`)
    }
  }, [])

  return (
    <TableOfContentsContent
      headings={headings}
      activeId={activeId}
      onHeadingClick={handleClick}
      isExpanded={isExpanded}
      onToggle={() => setIsExpanded(!isExpanded)}
      isMobile={false}
    />
  )
}

// ============================================================================
// Mobile TOC - Inline dropdown at top of content (hidden on lg+)
// ============================================================================

function MobileTableOfContents({ content, minHeadings = 2 }: TableOfContentsProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  const headings = useMemo(() => parseHeadings(content), [content])
  const activeId = useActiveHeading(headings)

  // Don't render if not enough headings
  if (headings.length < minHeadings) {
    return null
  }

  const handleClick = useCallback((id: string) => {
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      window.history.pushState(null, '', `#${id}`)
    }
    // Collapse TOC after clicking on mobile
    setIsExpanded(false)
  }, [])

  return (
    <TableOfContentsContent
      headings={headings}
      activeId={activeId}
      onHeadingClick={handleClick}
      isExpanded={isExpanded}
      onToggle={() => setIsExpanded(!isExpanded)}
      isMobile={true}
    />
  )
}

// ============================================================================
// Public API
// ============================================================================

export function TableOfContents({ content, minHeadings = 2 }: TableOfContentsProps) {
  return <DesktopTableOfContents content={content} minHeadings={minHeadings} />
}

export { DesktopTableOfContents, MobileTableOfContents }

/** Process content with heading IDs and desktop scroll margins */
export function processContentWithIds(content: string): string {
  const headings = parseHeadings(content)
  return injectHeadingIds(content, headings, false)
}

/** Process content with heading IDs and mobile scroll margins */
export function processContentWithIdsMobile(content: string): string {
  const headings = parseHeadings(content)
  return injectHeadingIds(content, headings, true)
}